import { createStorage } from '@aviata/storage';
import { freedomApp, debug } from 'freedom-shared';

import { sentryModel } from '@/shared/model';

/**
 * Normalizes a phone number for Accelera events by stripping a leading "+".
 * Downstream consumers expect the phone without the "+" (e.g. "77052208176",
 * not "+77052208176"). Returns undefined for nil input so the field is omitted.
 */
export const normalizePhone = (phone?: string | null): string | undefined => {
  if (phone == null) {
    return undefined;
  }
  return phone.replace(/^\+/, '');
};

const BRIDGE_REPORT_STORAGE_KEY = 'free-avia:accelera-bridge-missing-reported';

/**
 * Accelera events ride the native `window.FreedomApp.sendKafkaEvent` bridge, so
 * outside the super app (a plain browser, QA opening the URL directly) there is
 * nothing to send them to — expected, not a failure. Reporting that per event is
 * what flooded Sentry and got the logging dropped wholesale in SEARCH-323, and
 * the flood took the only signal of a *real* break with it: a native release
 * that renames or removes `sendKafkaEvent` now silently stops every
 * `booking_succeeded`, with nothing in Sentry to show for it.
 *
 * `window.FreedomApp` itself separates the two cases — absent means we are not
 * in the super app, present-but-missing-the-method means the native contract
 * broke. Only the latter is reported.
 *
 * The marker is persisted rather than kept in memory because a missing method
 * is not necessarily transient: `share`, for one, is documented as Android-only,
 * and every native build older than the kafka bridge lacks `sendKafkaEvent`
 * permanently. A per-session flag would report such a population once per
 * session forever — the old flood at a smaller constant. Once per device is the
 * cap that actually holds.
 */
let hasSyncedBridgeReport = false;

const reportMissingKafkaBridge = () => {
  if (hasSyncedBridgeReport || typeof window.FreedomApp === 'undefined') {
    return;
  }
  hasSyncedBridgeReport = true;

  const storage = createStorage(localStorage);
  if (storage.get(BRIDGE_REPORT_STORAGE_KEY)) {
    return;
  }
  storage.set(BRIDGE_REPORT_STORAGE_KEY, 'true');

  sentryModel.captureException(
    sentryModel.getReadableError(
      'ERR_MISSING_KAFKA_BRIDGE',
      new TypeError(
        'window.FreedomApp is present but exposes no sendKafkaEvent',
      ),
    ),
  );
};

/**
 * A working bridge re-arms the report, so a device that legitimately lacked the
 * method once still surfaces a *future* regression instead of staying quiet
 * forever on the strength of a stale marker.
 */
const forgetMissingKafkaBridge = () => {
  if (hasSyncedBridgeReport) {
    return;
  }
  hasSyncedBridgeReport = true;

  createStorage(localStorage).remove(BRIDGE_REPORT_STORAGE_KEY);
};

/**
 * Helper function to send Accelera (Kafka) events via JS Bridge
 */
export const sendAcceleraEvent = (
  eventName: string,
  params: Record<string, unknown> = {},
) => {
  debug(
    { prefix: 'Accelera Event', shouldLogInStageEnv: true },
    eventName,
    params,
  );

  if (typeof window.FreedomApp?.sendKafkaEvent !== 'function') {
    reportMissingKafkaBridge();
    return;
  }

  forgetMissingKafkaBridge();

  /**
   * Unlike the missing-bridge report above, this one is deliberately not
   * throttled: it only fires when the bridge exists and the call itself throws,
   * which is a code-level fault (a native method raising, a param that cannot
   * be serialized) rather than a whole population sitting on an old build. It
   * matches how `js-bridge/lib.ts` and `apps-flyer-events/index.ts` report the
   * sibling bridges.
   */
  freedomApp.lib.sendKafkaEvent(
    {
      name: eventName,
      product: 'aviata',
      params,
      backup: null,
    },
    (error: Error) =>
      sentryModel.captureException(
        sentryModel.getReadableError('ERR_SEND_KAFKA_EVENT', error),
      ),
  );
};
